import {FormControl, FormField, useFormContext} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';

import {useWatch} from 'react-hook-form';

import {useGetDirectSaleVariantsQuery} from '@dms/api/warehouse';
import {useGetSettingsServiceOrderVariantsQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {TestIdProps, suffixTestId} from 'shared/web';

import {DiscountFormType} from '../types/DiscountFormType';
import {getOptionsFromDirectSaleVariants} from '../utils/getOptionsFromDirectSaleVariants';

interface DiscountAllowedVariantProps extends TestIdProps {
  control: FormControl<DiscountFormType>;
}

export function DiscountAllowedVariant(props: DiscountAllowedVariantProps) {
  const {data: orderVariants, isLoading: isOrderVariantsLoading} =
    useGetSettingsServiceOrderVariantsQuery();
  const {data: directSaleVariants, isLoading: isDirectSaleVariantsLoading} =
    useGetDirectSaleVariantsQuery();

  const allowServiceOrderVariant = useWatch({
    control: props.control,
    name: 'isServiceOrderVariant',
  });
  const allowDirectSaleVariant = useWatch({control: props.control, name: 'isDirectSaleVariant'});

  const {trigger} = useFormContext<DiscountFormType>();

  // RHF revalidates only the changed field, so the sibling switch's "at least one variant"
  // error from superRefine would stay stale without re-triggering both.
  const revalidateVariantSwitches = () => trigger(['isServiceOrderVariant', 'isDirectSaleVariant']);

  return (
    <Grid columns={[1, 2]} spacing={4}>
      <VStack spacing={3}>
        <FormField
          control={props.control}
          name="isServiceOrderVariant"
          type="switch"
          label={i18n.t('entity.order.labels.serviceOrderVariant')}
          onChange={revalidateVariantSwitches}
          data-testid={suffixTestId('orderVariantSwitch', props)}
        />
        <Show when={allowServiceOrderVariant}>
          <FormField
            control={props.control}
            name="serviceOrderVariantIds"
            type="multiChoice"
            label={i18n.t('entity.order.labels.serviceOrderVariant')}
            options={orderVariants ?? []}
            isLoading={isOrderVariantsLoading}
            data-testid={suffixTestId('orderVariants', props)}
            isNotClearable
            closeMenuOnSelect={false}
          />
        </Show>
      </VStack>
      <VStack spacing={3}>
        <FormField
          control={props.control}
          name="isDirectSaleVariant"
          type="switch"
          label={i18n.t('entity.order.labels.directSaleVariants')}
          onChange={revalidateVariantSwitches}
          data-testid={suffixTestId('directSaleVariantSwitch', props)}
        />
        <Show when={allowDirectSaleVariant}>
          <FormField
            control={props.control}
            name="directSaleVariantIds"
            type="multiChoice"
            label={i18n.t('entity.order.labels.directSaleVariants')}
            options={getOptionsFromDirectSaleVariants(directSaleVariants ?? [])}
            isLoading={isDirectSaleVariantsLoading}
            data-testid={suffixTestId('directSaleVariants', props)}
            isNotClearable
            closeMenuOnSelect={false}
          />
        </Show>
      </VStack>
    </Grid>
  );
}
